import { Component, OnDestroy, OnInit } from '@angular/core';
import { Validators, FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { getFailureMessageRxOp } from '@geekbears/gb-ngrx-stack-base';
import { ModalService, NotificationsService } from '@geekbears/ngx-gb-ui-kit';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { isNil } from 'lodash';
import {
  addMultipleMembersFailureAction,
  addMultipleMembersSuccessAction,
  ClientsActions,
  addMultipleMembersLoadingSelector,
} from '@modules/company-manager/clients/application';
import {
  sendLinkFailureAction,
  sendLinkSuccessAction,
  uploadFileSuccessAction,
} from '@modules/company-manager/users/application';
import { filter, map, Observable, Subject, takeUntil, tap } from 'rxjs';
import { UsersModals } from '@modules/shared/presentation';
import { AppUser } from '@modules/company-manager/users/domain';

export interface Forms {
  email: string;
}
@Component({
  selector: 'app-add-users',
  templateUrl: './add-users.component.html',
  styleUrls: ['./add-users.component.scss'],
})
export class AddUsersComponent implements OnInit, OnDestroy {
  /**
   * @destroy$ is used to unsubscribe from all subscriptions
   */
  destroy$: Subject<void> = new Subject();

  duplicatedUsers: string[] = [];

  addMultipleMembersLoading$: Observable<boolean | any>;
  section: 'requester' | 'approver' = 'requester';

  isDisabled: boolean = false;
  alreadyExistsEmails: any[] = [];
  formsArrRequesters: any[] = [];
  formsArrApprovers: any[] = [];
  splittedFormsRequester!: any[];
  splittedFormsApprovers!: any[];
  currentCompanyId: string;
  submitted: boolean = false;

  /**
   * Requesters Forms
   */
  requestersForms: FormGroup[] = [];
  approversForms: FormGroup[] = [];
  constructor(
    private fb: FormBuilder,
    private store$: Store,
    private actions$: Actions,
    private notificationService: NotificationsService,
    private modalService: ModalService,
    private router: Router
  ) {
    this.addMultipleMembersLoading$ = this.store$
      .select(addMultipleMembersLoadingSelector)
      .pipe(
        tap((status) => {
          if (status) this.isDisabled = true;
        })
      );
    this.currentCompanyId =
      JSON.parse(localStorage.getItem('s_user') || '{}')._companyId || '';
  }

  ngOnInit() {
    this.requestersForms = [];
    this.approversForms = [];
    this.addForm();

    this.onFailureActions();
    this.onMultipleMembersAddedSuccessfully();
    this.onUsersUploadedSuccess();

    this.actions$
      .pipe(
        ofType(sendLinkSuccessAction),
        filter((action) => {
          return !isNil(action.response);
        }),
        map((action: any) => {
          if (action) {
            this.notificationService.success('Link successfully sent', {
              duration: 3000,
              icon: 'check-circle',
            });
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  onMultipleMembersAddedSuccessfully() {
    this.actions$
      .pipe(
        ofType(addMultipleMembersSuccessAction),
        filter((action) => {
          return !isNil(action.response);
        }),
        map((action: any) => {
          if (action) {
            if (action.response.data && action.response.message) {
              this.alreadyExistsEmails = action.response.data;
              this.isDisabled = false;
              this.onWarning(
                'There are already users using some emails from the list'
              );
            } else {
              this.router.navigateByUrl('dashboard/users');
              this.notificationService.success('Members Added Successfully', {
                duration: 3000,
                icon: 'check-circle',
              });
            }
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  // ngAfterViewInit(): void {
  //   this.splitFormRequesters();
  //   this.splitFormApprovers();
  // }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onUsersUploadedSuccess() {
    this.actions$
      .pipe(
        ofType(uploadFileSuccessAction),
        filter((action) => {
          return !isNil(action.response);
        }),
        map((action) => {
          const { data } = action.response;
          this.setUsers(data);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  setUsers(users: AppUser[]) {
    if (this.section === 'requester') {
      this.setRequestors(users);
    } else {
      this.setApprovers(users);
    }
  }

  setApprovers(users: any[]) {
    users.forEach((element, index) => {
      const { email } = element;
      this.approversForms[index].patchValue({
        email,
        _companyId: this.currentCompanyId,
        role: 'approver',
      });
      this.addForm();
    });
  }

  /**
   *
   * Set Requesters into the form
   */

  setRequestors(users: any[]) {
    users.forEach((element, index) => {
      const { email } = element;
      this.requestersForms[index].patchValue({
        email,
        _companyId: this.currentCompanyId,
        role: 'requester',
      });
      this.addForm();
    });
  }

  createMultipleMembers = async (members: any[]) => {
    if (members.length) {
      const duplicates = await this.validateDuplicated(members);
      const alreadyExist = await this.validateExistents(members);

      if (
        members &&
        this.isDisabled == false &&
        duplicates.length == 0 &&
        alreadyExist.length == 0
      )
        this.store$.dispatch(
          ClientsActions.addMultipleMembers({
            members: members,
          })
        );
      if (alreadyExist.length > 0)
        this.onWarning(
          'There are already users using some emails from the list'
        );
      if (duplicates.length > 0) this.onWarning('There Are Duplicated Emails');
    }
  };

  validateExistents = async (members: AppUser[]) => {
    let existents: any[] = [];

    members.forEach((member) => {
      let memberFound = [];
      memberFound = this.alreadyExistsEmails.filter(
        (email) => email == member.email
      );
      if (memberFound.length > 0) existents.push(memberFound);
    });

    return existents;
  };

  validateDuplicated = async (members: AppUser[]) => {
    let duplicates = [];
    for (let i = 0; i < members.length; i++) {
      for (let j = i + 1; j < members.length; j++) {
        {
          if (members[i].email == members[j].email) {
            duplicates.push(members[i].email);
            this.duplicatedUsers.push(members[i].email);
          }
        }
      }
    }
    return duplicates;
  };

  isThisDuplicated(userEmail: string) {
    let foundDuplicated = this.duplicatedUsers.filter(
      (email) => email == userEmail
    );

    if (foundDuplicated.length > 0) return true;
    else return false;
  }
  alreadyExists(userEmail: string) {
    let foundExistents = this.alreadyExistsEmails.filter(
      (email) => email == userEmail
    );

    if (foundExistents.length > 0) return true;
    else return false;
  }

  onFailureActions() {
    this.actions$
      .pipe(
        ofType(sendLinkFailureAction, addMultipleMembersFailureAction),
        map((action) => {
          return action.failure;
        }),
        getFailureMessageRxOp(),
        tap((errorMessage) => {
          setTimeout(() => {
            this.isDisabled = false;
          }, 1500);
          if (errorMessage) {
            this.notificationService.error(errorMessage, {
              duration: 5000,
              icon: 'alert-outline',
            });
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }
  importFile() {
    this.modalService.open(UsersModals, {
      data: {
        type: 'importFile',
      },
    });
  }
  changeStatus(email: string, index: number) {
    const duplicatedEmailIndex = this.duplicatedUsers.findIndex(
      (emails) => emails == email
    );
    const existantsEmailIndex = this.alreadyExistsEmails.findIndex(
      (emails) => emails == email
    );

    if (duplicatedEmailIndex >= 0)
      this.duplicatedUsers.splice(duplicatedEmailIndex, 1);
  }
  onWarning(message: string) {
    this.notificationService.error(message, { duration: 3000 });
  }

  addForm(): void {
    this.submitted = false;
    const form = this.fb.group({
      role: this.fb.control(
        this.section == 'approver' ? 'approver' : 'requester',
        [Validators.required, Validators.minLength(2)]
      ),
      _companyId: this.fb.control(this.currentCompanyId, [
        Validators.required,
        Validators.minLength(2),
      ]),
      email: this.fb.control('', [
        Validators.required,
        Validators.minLength(2),
        Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'),
      ]),
    });

    if (this.section === 'requester') this.requestersForms.push(form);
    else this.approversForms.push(form);
  }
  removeForm(index: number): void {
    if (this.section === 'requester') this.requestersForms.splice(index, 1);
    else this.approversForms.splice(index, 1);
  }
  async submitForms() {
    this.submitted = true;
    if (this.section === 'requester')
      this.createMultipleMembers(await this.validateDate(this.requestersForms));
    if (this.section === 'approver')
      this.createMultipleMembers(await this.validateDate(this.approversForms));
  }

  changeSection(section: 'requester' | 'approver') {
    this.section = section;
    if (section === 'approver' && !this.approversForms.length) this.addForm();
  }

  send(members: any) {}

  async validateDate(forms: FormGroup[]) {
    const usersWithIncompleteInfo: string[] = [];

    const completedUser = forms
      .map((form, index) => {
        if (
          form.value.email === '' ||
          form.value.role === ''
        ) {
          usersWithIncompleteInfo.push(`${index + 1}`);
        } else {
          return form.value;
        }
      })
      .filter((item) => {
        if (item) return item;
      });

    if (usersWithIncompleteInfo.length) {
      let message = `Review user${
        usersWithIncompleteInfo.length > 1 ? 's' : ''
      }  information`;
      this.onWarning(message);
      return [];
    }
    return completedUser;
  }
}
